import * as React from 'react';
import { PromptConfirmation } from 'ui';

import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import * as navigate from 'app/area/shared/navigation';

import { getDashboardAdminAccess } from '../common/get-dashboard-admin-access';
import { dashboardAdminTranslation } from '../constants';
import { DashboardAdminState } from '../types-store';

import { SavedReportsRenderer } from './components/saved-reports-renderer';

import { actionCreators } from './actions';
import { DashboardSavedReportsSelectorProps, selectors } from './selectors';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type DashboardSavedReportsProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & DashboardAdminState
  & DashboardSavedReportsSelectorProps
  & typeof actionCreators;

class DashboardSavedReportsComponent extends React.PureComponent<DashboardSavedReportsProps> {
  public static statePath: string = STATE.dashboardAdministration;

  public componentDidMount() {
    scrollToTop();
    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: DashboardSavedReportsProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }
  }

  public componentWillUnmount() {

    this.props.clearSavedReports();
  }

  private initForm = () => {
    const {
      id, searchFavouriteId, sharedState,
      loadSavedReports, displayError, translation
    } = this.props;

    const dashboardAccess = getDashboardAdminAccess(sharedState);
    if (!dashboardAccess) {
      displayError(translation.no_access_to_dashboard_admin);
      navigate.toStartupView(true);
      return;
    }
    loadSavedReports(id, searchFavouriteId);
  };

  private saveSavedReports = () => {
    const { id, searchFavouriteId, savedReports, saveSavedReports, translation } = this.props;

    saveSavedReports(id, searchFavouriteId, savedReports.savedReports,
      translation.dashboard_updated, translation.fields_are_filled_out_incorrectly);
  };

  public render() {

    const { id, searchFavouriteId, sharedState, savedReports, translation, 
      options, loadOptions, form: { validation },
      selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation,
      loadSavedReportEvaluation, updateSavedReport, deleteSavedReport,
      displayError, switchDisplayDisabled
    } = this.props;
    const locale = sharedState?.locale || '';

    return <>
      {!!this.props.shouldDisplayModal
        ? <PromptConfirmation
          title={translation.cancel_changes}
          description={translation.are_you_sure}
          continueText={translation.undo_all}
          cancelText={translation.go_back}
          onContinue={this.props.continue}
          onCancel={this.props.cancel} />
        : null}
      {id !== savedReports.typeId || !savedReports.savedReports || savedReports.fetching
        ? <SpinLoader />
        : <SavedReportsRenderer
          savedReportsState={savedReports}
          dashboardTypeId={id}
          searchFavouriteId={searchFavouriteId}
          selectCurrentSavedReports={selectCurrentSavedReports}
          updateCurrentSavedReports={updateCurrentSavedReports}
          updateCurrentValidation={updateCurrentValidation}

          loadDashboardEvaluation={loadSavedReportEvaluation}
          saveSavedReports={this.saveSavedReports}
          updateSavedReport={updateSavedReport}
          deleteSavedReport={deleteSavedReport}
          options={options}
          loadOptions={loadOptions}
          displayError={displayError}
          switchDisplayDisabled={switchDisplayDisabled}
          locale={locale}
          translation={translation}
          validation={validation} />}
    </>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(DashboardSavedReportsComponent)),
  actionCreators, selectors, dashboardAdminTranslation
);

export { container as DashboardSavedReports };
